import { Dip, HudDipItem } from "interfaces";
import { DateTime } from "luxon";
import { Moment } from "moment";
import { getTimeLeft } from "utils/time";

const HUD_SHOW_STORAGE_KEY =
	"store.solitairetripeaks.com:limitedDealHudOffersShown";

export const getHudAnalyticsValues = (
	hudItem: HudDipItem,
	nowTime: Moment | null
) => {
	const endTime = hudItem.dipConfig?.timerExpirationDate;
	const startTime = hudItem.dipConfig?.dip?.startTime;

	return {
		offer_id: hudItem.offerIds?.join(", "),
		time_left: getTimeLeft(nowTime, endTime, "seconds"),
		time_total: getTimeLeft(startTime, endTime, "seconds") ?? undefined
	};
};

export const shouldReportHudOfferShown = (
	hudItem: HudDipItem,
	userId: string | undefined,
	nowTime: Moment | null
): boolean => {
	const offerKey = hudItem.dipConfig?.id ?? hudItem.offerIds?.join("|");

	if (!offerKey || !userId || !nowTime) {
		return false;
	}

	const date = DateTime.fromMillis(nowTime.valueOf())
		.setZone("America/Los_Angeles")
		.toISODate();
	const storageKey = `${HUD_SHOW_STORAGE_KEY}:${userId}`;

	try {
		const storedValue = localStorage.getItem(storageKey);
		const storedData = storedValue
			? (JSON.parse(storedValue) as { date?: string; offers?: string[] })
			: undefined;
		const offers = storedData?.date === date ? storedData.offers ?? [] : [];

		if (offers.includes(offerKey)) {
			return false;
		}

		localStorage.setItem(
			storageKey,
			JSON.stringify({ date, offers: [...offers, offerKey] })
		);
		return true;
	} catch {
		return true;
	}
};

export const mapHudItemToDip = (hudItem: HudDipItem): Dip | undefined => {
	const { dipConfig, offerIds = [] } = hudItem;
	const dip = dipConfig?.dip;
	const buttonIds = dip?.buttonIds ?? [];

	if (
		!dipConfig?.id ||
		!dip?.id ||
		buttonIds.length === 0 ||
		buttonIds.length !== offerIds.length ||
		buttonIds.some((buttonId) => !buttonId) ||
		offerIds.some((offerId) => !offerId)
	) {
		return undefined;
	}

	const runtimeDip = dip as typeof dip & {
		isBogo?: boolean;
		isBuyItAll?: boolean;
	};
	const templateType = dip.templateType ?? "";
	const dipConfigWithoutHudTimer = { ...dipConfig };
	delete dipConfigWithoutHudTimer.timerExpirationDate;

	return {
		...dipConfigWithoutHudTimer,
		dip: {
			...dip,
			isBogo: runtimeDip.isBogo ?? templateType.toLowerCase().includes("bogo"),
			isBuyItAll:
				runtimeDip.isBuyItAll ??
				(dip.buyItAllDip === true || templateType === "BuyItAll")
		},
		buttons: buttonIds.map((buttonId, index) => ({
			id: buttonId,
			actionType: "offerPurchase",
			offerId: offerIds[index]
		}))
	} as Dip;
};
